import { SecureDocModel } from "../../models/secure-docs.model";
import { FileTypes } from "../../models/media.models";
import { useCallback, useEffect, useRef, useState } from "react";
import setToast from "../../helpers/toaster.helper";
import { ToasterType } from "../shared/toaster/toaster.model";
import { ToasterTexts } from "../shared/constants";
import { FalkonDocUploadIcon } from "../../icon";
import Sidebar from "./securedoc-sidebar.component";
import PDFContainer from "./main-pdfbuilder.component";
import { SecureDocsService } from "../../services/SecureDocsService";
import { AppRoutes } from "../routes/route.path";
import { useNavigate } from "react-router";
import { ProfileModel } from "../../models/profile.model";
import { UserService } from "../../services/UserService";
import io from "socket.io-client";
import { RecipientFormData } from "./stepper-upload.component";
import Config from "../../config";
import { useSelector } from "react-redux";
import * as selector from '../../selectors/selectors';

interface Props {
    recipientEmails?: RecipientFormData[];
    isUpdateMode?: boolean;
    fileID?: string;
    newFileID?: string;
    preservedRecipients?: RecipientFormData[];
    onRecipientsLoad?: (recipients: RecipientFormData[]) => void;
    pdfBase64?: string | null;
}

export interface FieldType {
    id: string;
    type: string;
    label: string;
    icon: JSX.Element;
}

export interface DroppedField {
    id: string;
    type: string;
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
    page?: number;
    assignedRecipient?: string;
    is_completed?: boolean;
    completed_at?: string;
}

export interface DragState {
    isDragging: boolean;
    dragType: "new" | "existing" | null;
    dragData: any;
    startPos: { x: number; y: number };
    currentPos: { x: number; y: number };
    offset: { x: number; y: number };
}

interface ConfiguredElement {
    element_id: string;
    element_type: string;
    placeholder: {
        page: number;
        x: number;
        y: number;
        width: number;
        height: number;
    };
    assigned_recipient: string;
    is_completed?: boolean;
    completed_at?: string;
}

interface FileConfigurationResponse {
    file_id: string;
    recipients: Array<{
        recipient_id: string;
        email: string;
        order_number: number;
        name?: string;
        phone?: string;
        is_invite_sent: boolean;
        is_completed: boolean;
    }>;
}

const DragAndDropComponent: React.FC<Props> = (props) => {
    const navigate = useNavigate();
    const isLoadingRef = useRef(false);
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const Profile: ProfileModel = UserService.getCurrentUserFromLocalStorage();
    const [droppedFields, setDroppedFields] = useState<DroppedField[]>([]);
    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
    const [selectedRecipient, setSelectedRecipient] = useState<RecipientFormData | null>(null);
    const [dragState, setDragState] = useState<DragState>({
        isDragging: false,
        dragType: null,
        dragData: null,
        startPos: { x: 0, y: 0 },
        currentPos: { x: 0, y: 0 },
        offset: { x: 0, y: 0 },
    });
    const [fileName, setFileName] = useState<string>("");
    const [fileType, setFileType] = useState<string>("");
    const [fileBase64, setFileBase64] = useState<string>("");
    const [recipientEmails, setRecipientEmails] = useState<RecipientFormData[]>(
        props.preservedRecipients || props.recipientEmails || []
    );

    const [loadingConfiguration, setLoadingConfiguration] = useState<boolean>(false);
    const [recipientRestrictions, setRecipientRestrictions] = useState<Map<string, { canEdit: boolean, canAdd: boolean }>>(new Map());
    const [configurationLoaded, setConfigurationLoaded] = useState<boolean>(false);
    const socketRef = useRef<any>(null);

    useEffect(() => {
        const fileId = props.fileID || props.newFileID;
        if (!fileId) return;

        const socket = io(`${Config.API_SECOND_URL}`, {
            transports: ["websocket", "polling"],
            query: {
                room: fileId
            },
        });

        socketRef.current = socket;

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    }, [props.fileID, props.newFileID]);

    const notifyConfigurationUpdate = (changeType: 'MODIFIED' | 'SAVED') => {
        if (socketRef.current) {
            const fileId = props.fileID || props.newFileID;
            if (!fileId) return;

            const currentUserEmail = Profile.Email || 'Administrator';

            const notificationData = {
                type: 'CONFIGURATION_UPDATED',
                changeType: changeType,
                fileId: fileId,
                adminEmail: currentUserEmail,
                timestamp: Date.now(),
                message: `Document configuration has been ${changeType.toLowerCase()} by ${currentUserEmail}.`,
                fieldsCount: droppedFields.length
            };

            socketRef.current.emit("chat message", JSON.stringify(notificationData));
        }
    };

    useEffect(() => {
        if (props.preservedRecipients && props.preservedRecipients.length > 0) {
            setRecipientEmails(props.preservedRecipients);

            if (!props.isUpdateMode && props.onRecipientsLoad) {
                props.onRecipientsLoad(props.preservedRecipients);
            }
        } else if (props.recipientEmails && props.recipientEmails.length > 0) {
            setRecipientEmails(props.recipientEmails);

            if (!props.isUpdateMode && props.onRecipientsLoad) {
                props.onRecipientsLoad(props.recipientEmails);
            }
        }
    }, [props.preservedRecipients, props.recipientEmails, props.isUpdateMode, props.onRecipientsLoad]);

    useEffect(() => {
        if (props.fileID && props.isUpdateMode) {
            loadExistingConfiguration();
        } else if (!props.isUpdateMode) {
            setConfigurationLoaded(true);
        }
    }, [props.fileID, props.isUpdateMode]);

    const loadExistingConfiguration = async () => {
        if (!props.fileID || isLoadingRef.current) return;

        isLoadingRef.current = true;
        setLoadingConfiguration(true);

        try {
            const response = await SecureDocsService.getFileConfigurations(props.fileID);
            const configurations = response?.data?.configurations;
            const configuredElements: ConfiguredElement[] = [];

            if (configurations && Array.isArray(configurations.elements)) {
                for (const element of configurations.elements) {
                    if (
                        element.element_id &&
                        element.element_type &&
                        element.placeholder &&
                        typeof element.placeholder.page === "number" &&
                        typeof element.placeholder.x === "number" &&
                        typeof element.placeholder.y === "number" &&
                        typeof element.placeholder.width === "number" &&
                        typeof element.placeholder.height === "number" &&
                        element.assigned_recipient?.recipient_id
                    ) {
                        configuredElements.push({
                            element_id: element.element_id,
                            element_type: element.element_type,
                            placeholder: {
                                page: element.placeholder.page,
                                x: element.placeholder.x,
                                y: element.placeholder.y,
                                width: element.placeholder.width,
                                height: element.placeholder.height,
                            },
                            assigned_recipient: element.assigned_recipient.recipient_id,
                            is_completed: element.is_completed,
                            completed_at: element.completed_at
                        });
                    }
                }
            }

            const recipient_response = await SecureDocsService.getSignedSecureDocUpdateInfo(props.fileID);
            const configRecipientData: FileConfigurationResponse = recipient_response.data;

            if (configRecipientData.recipients && Array.isArray(configRecipientData.recipients)) {
                const updatedRecipients: RecipientFormData[] = configRecipientData.recipients.map(r => ({
                    recipient_id: r.recipient_id,
                    email: r.email,
                    order_number: r.order_number,
                    name: r.name || '',
                    phone: r.phone || '',
                    type: 'Email OTP',
                    is_completed: r.is_completed,
                    is_invite_sent: r.is_invite_sent,
                }));

                setRecipientEmails(updatedRecipients);

                if (props.onRecipientsLoad) {
                    props.onRecipientsLoad(updatedRecipients);
                }

                const restrictions = new Map();
                configRecipientData.recipients.forEach(recipient => {
                    restrictions.set(recipient.recipient_id, {
                        canEdit: !recipient.is_completed,
                        canAdd: !recipient.is_completed
                    });
                });
                setRecipientRestrictions(restrictions);
            }

            if (configuredElements && Array.isArray(configuredElements)) {
                const convertedFields: DroppedField[] = configuredElements.map(element => ({
                    id: element.element_id,
                    type: element.element_type.toLowerCase(),
                    label: element.element_type,
                    x: element.placeholder.x,
                    y: element.placeholder.y,
                    width: element.placeholder.width,
                    height: element.placeholder.height,
                    page: element.placeholder.page,
                    assignedRecipient: element.assigned_recipient,
                    is_completed: element.is_completed,
                    completed_at: element.completed_at
                }));

                setDroppedFields(convertedFields);

                if (configRecipientData.recipients && configRecipientData.recipients.length > 0) {
                    const firstRecipient: RecipientFormData = {
                        recipient_id: configRecipientData.recipients[0].recipient_id,
                        email: configRecipientData.recipients[0].email,
                        order_number: configRecipientData.recipients[0].order_number,
                        name: configRecipientData.recipients[0].name || '',
                        phone: configRecipientData.recipients[0].phone || '',
                        type: 'Email OTP'
                    };
                    setSelectedRecipient(firstRecipient);
                }
            }
            setConfigurationLoaded(true);
            setToast("Configuration loaded successfully", ToasterType.SUCCESS);

        } catch (error) {
            setToast("Failed to load existing configuration", ToasterType.DANGER);
        } finally {
            setLoadingConfiguration(false);
            isLoadingRef.current = false;
        }
    };

    const handleConfiguredFieldsLoad = useCallback((fields: DroppedField[], recipients: any[]) => {
        if (fields.length > 0 && !configurationLoaded) {
            setDroppedFields(fields);
        }

        if (recipients.length > 0 && recipientEmails.length === 0) {
            const formattedRecipients: RecipientFormData[] = recipients.map(r => ({
                recipient_id: r.recipient_id,
                email: r.email,
                order_number: r.order_number,
                name: r.name || '',
                phone: r.phone || '',
                type: r.type || 'Email OTP'
            }));

            setRecipientEmails(formattedRecipients);

            if (!props.isUpdateMode && props.onRecipientsLoad) {
                props.onRecipientsLoad(formattedRecipients);
            }
        }
    }, [configurationLoaded, recipientEmails.length, props.isUpdateMode, props.onRecipientsLoad]);

    const canEditField = useCallback((fieldRecipientId: string): boolean => {
        if (!fieldRecipientId || !recipientRestrictions.has(fieldRecipientId)) return true;
        const restrictions = recipientRestrictions.get(fieldRecipientId);
        return restrictions?.canEdit !== false;
    }, [recipientRestrictions]);

    const canAddFieldForRecipient = useCallback((recipientId: string): boolean => {
        if (!recipientId || !recipientRestrictions.has(recipientId)) return true;
        const restrictions = recipientRestrictions.get(recipientId);
        return restrictions?.canAdd !== false;
    }, [recipientRestrictions]);

    const isInteractionDisabled = loadingConfiguration || (props.isUpdateMode && !configurationLoaded);

    const handleNewFieldDragStart = useCallback((
        field: FieldType,
        startPos: { x: number; y: number },
        offset: { x: number; y: number }
    ) => {
        if (isInteractionDisabled) return;

        if (!selectedRecipient) {
            setToast("Please select a recipient before adding fields", ToasterType.WARNING);
            return;
        }

        if (!canAddFieldForRecipient(selectedRecipient.recipient_id)) {
            setToast("Cannot add fields for recipients who have been invited", ToasterType.DANGER);
            return;
        }

        setDragState({
            isDragging: true,
            dragType: "new",
            dragData: field,
            startPos,
            currentPos: startPos,
            offset,
        });
    }, [isInteractionDisabled, selectedRecipient, canAddFieldForRecipient]);

    const handleExistingFieldDragStart = useCallback((
        field: DroppedField,
        startPos: { x: number; y: number }
    ) => {
        if (isInteractionDisabled) return;

        if (field.assignedRecipient && !canEditField(field.assignedRecipient)) {
            setToast("Cannot modify fields for recipients who have been invited", ToasterType.DANGER);
            return;
        }

        setDragState({
            isDragging: true,
            dragType: "existing",
            dragData: field,
            startPos,
            currentPos: startPos,
            offset: { x: 0, y: 0 },
        });
    }, [isInteractionDisabled, canEditField]);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (dragState.isDragging && !isInteractionDisabled) {
                const newPos = { x: e.clientX, y: e.clientY };
                setDragState((prev) => ({ ...prev, currentPos: newPos }));

                if (dragState.dragType === "existing") {
                    const deltaX = e.clientX - dragState.startPos.x;
                    const deltaY = e.clientY - dragState.startPos.y;
                    const field = dragState.dragData as DroppedField;

                    if (!field.assignedRecipient || canEditField(field.assignedRecipient)) {
                        setDroppedFields((prev) => {
                            const updatedFields = prev.map((f) =>
                                f.id === field.id
                                    ? { ...f, x: field.x + deltaX, y: field.y + deltaY }
                                    : f
                            );
                            return updatedFields;
                        });
                    }
                }
            }
        },
        [dragState, isInteractionDisabled, canEditField, configurationLoaded]
    );

    const handleMouseUp = useCallback(() => {
        setDragState({
            isDragging: false,
            dragType: null,
            dragData: null,
            startPos: { x: 0, y: 0 },
            currentPos: { x: 0, y: 0 },
            offset: { x: 0, y: 0 },
        });
    }, []);

    useEffect(() => {
        if (dragState.isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };
        }
    }, [dragState.isDragging, handleMouseMove, handleMouseUp]);

    const handleFieldDrop = useCallback((
        field: FieldType,
        position: { x: number; y: number },
        page: number
    ) => {
        if (isInteractionDisabled) return;

        if (!selectedRecipient) {
            setToast("Please select a recipient before adding fields", ToasterType.WARNING);
            return;
        }

        if (!canAddFieldForRecipient(selectedRecipient.recipient_id)) {
            setToast("Cannot add fields for recipients who have been invited", ToasterType.DANGER);
            return;
        }

        const adjustedX = position.x - dragState.offset.x;
        const adjustedY = position.y - dragState.offset.y;

        const newField: DroppedField = {
            id: `${field.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: field.type,
            label: field.label,
            x: Math.max(0, adjustedX), 
            y: Math.max(0, adjustedY),
            width: field.type === "signature" ? 200 : 150,
            height: field.type === "signature" ? 50 : 40,
            page: page,
            assignedRecipient: selectedRecipient.recipient_id,
            is_completed: false,
            completed_at: "0001-01-01T00:00:00Z"
        };

        setDroppedFields((prev) => [...prev, newField]);
        setSelectedFieldId(newField.id);
    }, [isInteractionDisabled, selectedRecipient, configurationLoaded, canAddFieldForRecipient]);

    const updateField = useCallback((id: string, updates: Partial<DroppedField>) => {
        if (isInteractionDisabled) return;

        const field = droppedFields.find(f => f.id === id);
        if (field?.assignedRecipient && !canEditField(field.assignedRecipient)) {
            setToast("Cannot modify fields for recipients who have been invited", ToasterType.DANGER);
            return;
        }

        setDroppedFields((prev) =>
            prev.map((field) => (field.id === id ? { ...field, ...updates } : field))
        );

    }, [isInteractionDisabled, droppedFields, configurationLoaded, canEditField]);

    const deleteField = useCallback((id: string) => {
        if (isInteractionDisabled) return;

        const field = droppedFields.find(f => f.id === id);
        if (field?.assignedRecipient && !canEditField(field.assignedRecipient)) {
            setToast("Cannot delete fields for recipients who have been invited", ToasterType.DANGER);
            return;
        }

        setDroppedFields((prev) => prev.filter((field) => field.id !== id));
        if (selectedFieldId === id) {
            setSelectedFieldId(null);
        }

    }, [isInteractionDisabled, droppedFields, selectedFieldId, configurationLoaded, canEditField]);

    const handleRecipientChange = useCallback((recipient: RecipientFormData | null) => {
        setSelectedRecipient(recipient);
    }, []);

    const saveDocumentConfiguration = async () => {
        const file_id = props.fileID || props.newFileID;
        if (!file_id) {
            setToast("File ID is required", ToasterType.DANGER);
            return;
        }

        if (!droppedFields || droppedFields.length === 0) {
            setToast(ToasterTexts.SECURE_DOC_SELECT_FIELD, ToasterType.WARNING);
            return;
        }

        const unassignedFields = droppedFields.filter(field => !field.assignedRecipient);
        if (unassignedFields.length > 0) {
            setToast(`${unassignedFields.length} field(s) need to be assigned to recipients`, ToasterType.DANGER);
            return;
        }

        try {
            const payload = props.isUpdateMode
                ? {
                    file_id: file_id,
                    elements: droppedFields.map((field) => ({
                        element_type: field.type.toLowerCase(),
                        placeholder: {
                            page: field.page || 1,
                            x: Math.round(field.x),
                            y: Math.round(field.y),
                            width: Math.round(field.width),
                            height: Math.round(field.height),
                        },
                        assigned_recipient: field.assignedRecipient,
                        is_completed: field.is_completed !== undefined ? field.is_completed : false,
                        completed_at: field.completed_at || "0001-01-01T00:00:00Z",
                    }))
                }
                : {
                    user_id: Profile.ID,
                    elements: droppedFields.map((field) => ({
                        element_type: field.type.charAt(0).toUpperCase() + field.type.slice(1),
                        placeholder: {
                            page: field.page || 1,
                            x: Math.round(field.x),
                            y: Math.round(field.y),
                            width: Math.round(field.width),
                            height: Math.round(field.height),
                        },
                        assigned_recipient: field.assignedRecipient,
                    }))
                };

            const response = props.isUpdateMode
                ? await SecureDocsService.modifyFileConfigurations(file_id, payload)
                : await SecureDocsService.setFileConfigurations(file_id, payload);

            const changeType = props.isUpdateMode ? 'MODIFIED' : 'SAVED';
            notifyConfigurationUpdate(changeType);

            setToast(
                (props.isUpdateMode ? ToasterTexts.SECURE_DOC_UPDATE_CONFIGURATION_SUCCESS : ToasterTexts.SECURE_DOC_SAVE_CONFIGURATION_SUCCESS),
                ToasterType.SUCCESS
            );

            if (file_id) {
                navigateToSuccessPage(file_id);
            }

        } catch (error) {
            setToast(ToasterTexts.SECURE_DOC_SAVE_CONFIGURATION_FAILED, ToasterType.DANGER);
        }
    };

    const navigateToSuccessPage = (id: string) => {
        navigate(`${AppRoutes.FALKON_DOCS_SUCCESS}?fileId=${id}`);
    };

    if (loadingConfiguration) {
        return (
            <div className="d-flex justify-center align-center t-align-center p-relative h-v-100">
                <div className="load-config-container">
                    <div className="loading-spinner mx-auto"></div>
                    <p className="success-page-text pt-2 pl-3 color-primary-accent h4-semibold">Loading Document Configuration...</p>
                    <p className="success-page-text pt-1 pl-3 color-primary-accent h4-semibold">Please wait while we retrieve your document and field configurations</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto">
            <div className={`main-container ${IsMobileDevice ? 'o-y-scroll h-v-91' : ''}`}>
                <Sidebar
                    onFieldDragStart={handleNewFieldDragStart}
                    isUpdateMode={props.isUpdateMode}
                    fileID={props.fileID}
                    droppedFields={droppedFields}
                    selectedRecipient={selectedRecipient}
                    onRecipientChange={handleRecipientChange}
                    recipientEmails={recipientEmails}
                    onSaveDocument={saveDocumentConfiguration}
                    recipientRestrictions={recipientRestrictions}
                />

                <div className="">
                    <PDFContainer
                        droppedFields={droppedFields}
                        selectedFieldId={selectedFieldId}
                        onFieldDrop={handleFieldDrop}
                        onFieldUpdate={updateField}
                        onFieldDelete={deleteField}
                        onFieldSelect={setSelectedFieldId}
                        onFieldDragStart={handleExistingFieldDragStart}
                        dragState={dragState}
                        recipientRestrictions={recipientRestrictions}
                        fileID={props.fileID}
                        onConfiguredFieldsLoad={handleConfiguredFieldsLoad}
                        recipientEmails={recipientEmails}
                        pdfBase64={props.pdfBase64}
                    />
                </div>
            </div>
        </div>
    );
};

export default DragAndDropComponent;